import { useLocation } from "wouter";
import { DaySummary } from "./DaySummary";
import { ListHeader } from "./ListHeader";
import { selectNearbySpots } from "../lib/nearby";
import type { EnrichedSpot, SpeedUnit } from "../lib/types";
import styles from "./NearbySpots.module.css";

interface NearbySpotsProps {
  /** The spot whose detail page this is — excluded and used as the origin. */
  current: EnrichedSpot;
  /** Full curated pool to pick neighbours from. */
  spots: readonly EnrichedSpot[];
  /** Day the detail page is currently aligned to; drives ranking + column. */
  selectedDay: number;
  unit: SpeedUnit;
}

function fmtDistance(km: number): string {
  if (km < 1) return "<1 km";
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km)} km`;
}

/**
 * "Nearby spots" list on the detail page — the closest other spots, nearest
 * first. Each row mirrors the search-overview card with a single forecast
 * column for the selected day. Renders nothing when there are no spots within
 * range.
 */
export function NearbySpots({ current, spots, selectedDay, unit }: NearbySpotsProps) {
  const [, setLocation] = useLocation();
  const items = selectNearbySpots(current, spots, selectedDay);
  if (items.length === 0) return null;

  return (
    <section className={styles.nearby}>
      <ListHeader>Nearby spots</ListHeader>
      <ul className={styles.list}>
        {items.map(({ spot, day, distanceKm }) => (
          <li key={spot.id}>
            <button
              type="button"
              className={styles.row}
              onClick={() => setLocation(`/spot/${spot.id}?day=${selectedDay}`)}
            >
              <span className={styles.rowLeft}>
                <span className={styles.meta}>
                  <span className={styles.area}>{spot.area}</span>
                  <span className={styles.dot} aria-hidden>
                    ·
                  </span>
                  <span className={styles.distance}>{fmtDistance(distanceKm)}</span>
                </span>
                <span className={styles.name}>{spot.name}</span>
              </span>
              <DaySummary day={day} unit={unit} idealDirs={spot.ideal.dirs} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
